import { Form, message, Modal } from 'antd';
import styled from 'styled-components';
import Text from 'components/Text';
import RequestForm, { RequestFormValues } from '../AddRequestModal/RequestForm';
import Button from 'components/Button';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import requestSupportApis from 'services/requestSupportApis';
import CommentAndActivity from './CommentAndActivity';
import { IDetailRequestSupport } from 'pages/request-support/interfaces/apis';

import ModalConfirm, { ModalConfirmRef } from 'pages/employees/components/ModalConfirm';
export interface DetailRequestModalRef {
  open: (ticketId: string) => void;
}

interface Props {
  refetch?: () => void;
}
const DetailRequestModal = forwardRef<DetailRequestModalRef, Props>(({ refetch }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [detailData, setDetailData] = useState<IDetailRequestSupport | null>(null);
  const [form] = Form.useForm<RequestFormValues>();
  const modalConfirmRef = useRef<ModalConfirmRef>(null);
  const [note, setNote] = useState('');

  const fetchDetailRequestSupport = async (ticketId: string) => {
    try {
      const res: AxiosResponse = await requestSupportApis.getDetailRequestSupport(ticketId);

      if (res?.data?.data) {
        setDetailData(res.data.data);
        form.setFieldsValue({
          id: ticketId,
          shopId: res.data.data.shopId,
          assignId: res.data.data.assignId,
          status: res.data.data.status,
          title: res.data.data.title,
          description: res.data.data.description,
          priority: res.data.data.priority,
        });
      }
    } catch (error) {
      message.error('Failed to fetch detail support ticket');
    }
  };

  useImperativeHandle(ref, () => ({
    open: (ticketId: string) => {
      setIsOpen(true);
      fetchDetailRequestSupport(ticketId);
    },
  }));

  const handleClose = () => {
    setIsOpen(false);
    setDetailData(null);
    setNote('');
    form.resetFields();
  };

  const handleAddComment = async (note: string) => {
    try {
      const res: AxiosResponse = await requestSupportApis.addComment(detailData?.id || '', note);

      if (res?.data?.data) {
        fetchDetailRequestSupport(detailData?.id || '');
        setNote('');
      }
    } catch (error) {
      message.error('Failed to add comment');
    }
  };

  const onDelete = async (ticketId: string) => {
    try {
      const res: AxiosResponse = await requestSupportApis.deleteRequestSupport(ticketId);

      if (res?.data?.data) {
        message.success('Support ticket deleted successfully');
        handleClose();
        modalConfirmRef.current?.close();
        if (refetch) {
          refetch();
        }
      }
    } catch (error) {
      message.error('Failed to delete support ticket');
    }
  };

  const handleDelete = () => {
    modalConfirmRef.current?.open(
      'Are you sure you want to delete this support ticket?',
      'This action cannot be undone.',
      () => onDelete(detailData?.id || ''),
    );
  };

  return (
    <StyledModal open={isOpen} onCancel={handleClose} footer={null} width={1080} closable={false}>
      <Container>
        <LeftContent>
          <Text variant='H6' style={{ fontWeight: 'bold' }}>
            Detail Support Ticket
          </Text>
          <RequestForm form={form} onClose={handleClose} refetch={refetch} />
        </LeftContent>
        <RightContent>
          <CommentAndActivity
            comments={detailData?.comments}
            handleAddComment={handleAddComment}
            note={note}
            setNote={setNote}
          />
        </RightContent>
      </Container>
      <Footer>
        <Button variant='DEFAULT' onClick={handleClose} style={{ flex: 1 }} size='middle' htmlType='button'>
          Close
        </Button>
        <Button variant='LIGHT' onClick={handleDelete} style={{ flex: 1 }} size='middle' htmlType='button'>
          Delete
        </Button>
        <Button variant='PRIMARY' style={{ flex: 1 }} size='middle' htmlType='submit' onClick={() => form.submit()}>
          Update
        </Button>
      </Footer>
      <ModalConfirm ref={modalConfirmRef} />
    </StyledModal>
  );
});

export default DetailRequestModal;

const Container = styled.div`
  display: flex;
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
  }
`;

const LeftContent = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  max-width: 600px;
  width: 100%;
`;

const RightContent = styled.div`
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #f6f7fc;
`;

const Footer = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  gap: 24px;
  background: #fff;
  box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.15);
  padding: 16px 24px 24px;

  .ant-btn {
    font-family: 'Poppins';
    font-weight: 500;
  }
`;
